import angular from 'angular'

export interface PropertyTestFunctionInput {
  readonly general: {
    readonly try_hub: boolean
    readonly diagnostics: boolean
  }
  readonly unchangedSettings: {
    readonly try_hub: boolean
    readonly diagnostics: boolean
  }
  readonly propertyName: 'try_hub' | 'diagnostics'
}

export const isPropertyToggledOff = ({
  general,
  unchangedSettings,
  propertyName
}: PropertyTestFunctionInput) => {
  return (
    isPropertyChanged({ general, unchangedSettings, propertyName }) &&
    !general[propertyName]
  )
}

export const isPropertyOffOrUnchanged = ({
  general,
  unchangedSettings,
  propertyName
}: PropertyTestFunctionInput) => {
  return (
    !general[propertyName] ||
    !isPropertyChanged({ general, unchangedSettings, propertyName })
  )
}

export const isPropertyChanged = ({
  general,
  unchangedSettings,
  propertyName
}: PropertyTestFunctionInput) => {
  return general[propertyName] !== unchangedSettings[propertyName]
}

export const isProxyUnchanged = ({
  general,
  unchangedSettings
}: {
  readonly general: GeneralInternetSettings
  readonly unchangedSettings: GeneralInternetSettings
}) => {
  return (
    angular.isDefined(unchangedSettings) &&
    general.use_proxy === unchangedSettings.use_proxy &&
    general.proxy_user === unchangedSettings.proxy_user &&
    general.proxy_host === unchangedSettings.proxy_host &&
    general.proxy_port === unchangedSettings.proxy_port &&
    general.proxy_password === unchangedSettings.proxy_password
  )
}

export const isOnlyServiceToggledOff = ({
  form,
  general,
  unchangedSettings
}: {
  readonly form: {
    readonly $dirty: angular.IFormController['$dirty']
  }
  readonly general: GeneralInternetSettings
  readonly unchangedSettings: GeneralInternetSettings
}) => {
  return (
    form.$dirty &&
    isProxyUnchanged({
      general,
      unchangedSettings
    }) &&
    ((isPropertyToggledOff({
      general,
      unchangedSettings,
      propertyName: 'try_hub'
    }) &&
      isPropertyOffOrUnchanged({
        general,
        unchangedSettings,
        propertyName: 'diagnostics'
      })) ||
      (isPropertyToggledOff({
        general,
        unchangedSettings,
        propertyName: 'diagnostics'
      }) &&
        isPropertyOffOrUnchanged({
          general,
          unchangedSettings,
          propertyName: 'try_hub'
        })))
  )
}

interface SuperadminGeneralInternetCtrlType {
  general: GeneralInternetSettings
  hubError: boolean
  diagnosticsError: boolean
  diagnosticsConnected: boolean
  model: {
    loading: boolean
  }
  form: angular.IFormController
}

export function SuperadminGeneralInternetCtrl(
  this: SuperadminGeneralInternetCtrlType,
  $q: {
    readonly all: angular.IQService['all']
  },
  $translate: {
    readonly instant: LegacyTranslateService['instant']
  },
  confirmModal: LegacyConfirmService,
  superadminGeneralService: LegacySuperadminGeneralService
) {
  'ngInject'

  let testSuccess = false
  let unchangedSettings: GeneralInternetSettings

  angular.extend(this, {
    model: {
      loading: false
    },
    hubError: false,
    diagnosticsError: false,
    diagnosticsConnected: false
  })

  const test = () => {
    const testRequests: {
      hub?: angular.IPromise<any>
      diagnostics?: angular.IPromise<any>
    } = {}
    let testingHub = false
    let testingDiagnostics = false
    if (!(this.general.try_hub || this.general.diagnostics)) {
      return
    }

    this.model.loading = true

    if (this.general.try_hub) {
      testRequests.hub = superadminGeneralService.testHub(_getSettings())
      testingHub = true
    }
    if (this.general.diagnostics) {
      testRequests.diagnostics = superadminGeneralService.testDiagnostics(
        _getSettings()
      )
      testingDiagnostics = true
    }

    return (
      $q
        // The TypeScript declaration for angular.IQService['all'] that uses the "object" argument
        // does not allow undefined object properties, but it is a valid case, so we need to
        // explicitly define the type here:
        .all(
          testRequests as {
            hub: angular.IPromise<any>
            diagnostics: angular.IPromise<any>
          }
        )
        .then(
          (responses: {
            readonly hub?: boolean
            readonly diagnostics?: boolean
          }) => {
            this.hubError = testingHub && !responses.hub
            this.diagnosticsError = testingDiagnostics && !responses.diagnostics
            this.diagnosticsConnected =
              testingDiagnostics && !!responses.diagnostics
            testSuccess = !(this.hubError || this.diagnosticsError)

            if (this.diagnosticsConnected) {
              this.general.diagnostics_last_connected = Date.valueOf()
            }

            if (testSuccess) {
              unchangedSettings.use_proxy = this.general.use_proxy
              unchangedSettings.proxy_user = this.general.proxy_user
              unchangedSettings.proxy_host = this.general.proxy_host
              unchangedSettings.proxy_port = this.general.proxy_port
              unchangedSettings.proxy_password = this.general.proxy_password

              this.form.$setPristine()
            }
          }
        )
        .finally(() => {
          this.model.loading = false
        })
    )
  }

  function _init() {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    _getHubSettings()
  }

  const save = () => {
    return confirmModal
      .open({
        title: $translate.instant('title_pleaseConfirmAction'),
        message: $translate.instant(
          'detail_saveGeneralPropertiesDialog_updateConfirmation'
        ),
        dialogOptions: {
          size: 'sm',
          yes: $translate.instant('action_update'),
          no: $translate.instant('action_cancel')
        }
      })
      .then((response) => {
        if (response) {
          return _updateProperties().then(() => {
            this.form.$setPristine()
          })
        }
      })
  }

  const testValidated = () => {
    return testSuccess && !this.form.$dirty
  }

  function saveDisabled() {
    return !_canSave()
  }

  const _canSave = () => {
    return (
      (testValidated() &&
        isProxyUnchanged({
          general: this.general,
          unchangedSettings
        })) ||
      isOnlyServiceToggledOff({
        form: this.form,
        general: this.general,
        unchangedSettings
      })
    )
  }

  const onServiceChange = () => {
    if (
      !this.general.try_hub &&
      (angular.isUndefined(this.general.diagnostics) ||
        !this.general.diagnostics)
    ) {
      this.general.use_proxy = false
    }
  }

  const _getHubSettings = () => {
    return superadminGeneralService
      .getGeneralHubProperties()
      .then((general) => {
        unchangedSettings = angular.copy(general)
        this.general = general
      })
  }

  const _updateProperties = () => {
    return superadminGeneralService
      .updateGeneralHubProperties(_getSettings())
      .then(() => {
        unchangedSettings = angular.copy(this.general)
      })
  }

  const _getSettings = () => {
    const form = this.general
    if (form.use_proxy) {
      if (angular.isUndefined(form.proxy_user)) {
        form.proxy_user = ''
      }
      if (angular.isUndefined(form.proxy_password)) {
        form.proxy_password = ''
      }
    }
    return form
  }

  _init()

  angular.extend(this, {
    save,
    test,
    testValidated,
    saveDisabled,
    onServiceChange
  })
}
